import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'

export type ThemeMode = 'system' | 'light' | 'dark'
export type Theme = Exclude<ThemeMode, 'system'>

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly themeStorageKey = 'app-theme-mode'
  private readonly systemThemeQuery = window.matchMedia('(prefers-color-scheme: dark)')
  private readonly themeSubject = new BehaviorSubject<Theme>('light')
  private currentThemeMode: ThemeMode = 'system'
  private currentTheme: Theme = 'light'

  private readonly onSystemThemeChange = (): void => {
    this.applyThemeAttribute(this.currentThemeMode)
    this.emitTheme(this.currentTheme)
  }

  constructor() {
    this.currentThemeMode = this.getPersistedThemeMode()
    this.applyThemeAttribute(this.currentThemeMode)
    this.emitTheme(this.currentTheme)
    this.systemThemeQuery.addEventListener('change', this.onSystemThemeChange)
  }

  getTheme(): Theme {
    return this.currentTheme
  }

  setThemeMode(themeMode: ThemeMode): void {
    this.currentThemeMode = themeMode
    window.localStorage.setItem(this.themeStorageKey, themeMode)
    this.applyThemeAttribute(themeMode)
    this.emitTheme(this.currentTheme)
  }

  getTheme$(): Observable<Theme> {
    return this.themeSubject.asObservable()
  }

  getPersistedThemeMode(): ThemeMode {
    const storedTheme = window.localStorage.getItem(this.themeStorageKey)
    return storedTheme === 'light' || storedTheme === 'dark' || storedTheme === 'system'
      ? storedTheme
      : 'system'
  }

  private applyThemeAttribute(themeMode: ThemeMode): void {
    const root = document.documentElement
    if (themeMode === 'system') {
      const resolvedTheme: Theme = this.systemThemeQuery.matches ? 'dark' : 'light'
      this.currentTheme = resolvedTheme
      root.setAttribute('data-theme', resolvedTheme)
    } else {
      this.currentTheme = themeMode
      root.setAttribute('data-theme', themeMode)
    }
  }

  private emitTheme(theme: Theme): void {
    this.themeSubject.next(theme)
  }
}
